import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DefaultOptionType } from 'antd/es/select';
import { Form, Input, InputNumber, Select, Spin } from 'antd';
import { CellAdditionProps, EditableContext } from './types';
import { useDeferredInlineEdit } from './InlineEditApplyContext';

interface EditableCellProps<T> extends CellAdditionProps<T> {
  record: T;
  rowKey: string;
  onUpdate?: (oldData: T, newData: Partial<T>) => void;
}

export const EditableCell: React.FC<
  React.PropsWithChildren<EditableCellProps<any>>
> = ({
  dataIndex,
  editable,
  renderType,
  renderOptions,
  optionsLoading,
  rules,
  record,
  rowKey,
  onUpdate,
  clearFieldsOnEdit,
  children,
  ...restProps
}) => {
  const form = useContext(EditableContext)!;
  const deferredEdit = useDeferredInlineEdit<Record<string, unknown>>();
  const clearDeferredRow = deferredEdit?.clearRow;
  const [options, setOptions] = useState<DefaultOptionType[]>([]);
  const isEditable =
    typeof editable === 'function' ? editable(record) : editable;
  const dataIndexKey = String(dataIndex);
  const deferUpdate = deferredEdit?.enabled ?? false;

  const pendingChanges = deferredEdit?.getRowChanges?.(rowKey);
  const effectiveRecord = useMemo(
    () => (pendingChanges ? { ...record, ...pendingChanges } : record),
    [pendingChanges, record],
  );

  useEffect(() => {
    if (!renderOptions) return;
    const list =
      typeof renderOptions === 'function'
        ? renderOptions(effectiveRecord)
        : renderOptions;
    setOptions(list);
  }, [effectiveRecord, renderOptions]);

  const isOptionsLoading =
    typeof optionsLoading === 'function'
      ? optionsLoading(effectiveRecord)
      : !!optionsLoading;

  const recordValue = record?.[dataIndex];

  useEffect(() => {
    if (!record) return;
    const pendingValue = pendingChanges?.[dataIndexKey];
    form.setFieldsValue({
      [dataIndexKey]: pendingValue !== undefined ? pendingValue : recordValue,
    });
  }, [dataIndexKey, form, pendingChanges, record, recordValue]);

  useEffect(() => {
    clearDeferredRow?.(rowKey);
  }, [clearDeferredRow, recordValue, rowKey]);

  const save = useCallback(
    async (value?: unknown) => {
      let nextValue = value;
      if (nextValue === undefined) {
        if (!deferUpdate) {
          await form.validateFields([dataIndexKey]);
        }
        nextValue = form.getFieldValue(dataIndexKey);
      }

      const clearedFields = clearFieldsOnEdit?.length
        ? Object.fromEntries(
            clearFieldsOnEdit.map((field) => [String(field), null]),
          )
        : null;

      if (deferUpdate) {
        deferredEdit?.markChange(
          rowKey,
          record,
          dataIndexKey,
          nextValue as Record<string, unknown>[string],
          clearFieldsOnEdit?.length
            ? { clearFields: clearFieldsOnEdit as string[] }
            : undefined,
        );
        if (clearedFields) {
          form.setFieldsValue(clearedFields);
        }
        return;
      }

      if (clearedFields) {
        form.setFieldsValue(clearedFields);
      }

      onUpdate?.(record, {
        [dataIndexKey]: nextValue,
        ...clearedFields,
      } as Partial<typeof record>);
    },
    [
      clearFieldsOnEdit,
      dataIndexKey,
      deferUpdate,
      deferredEdit,
      form,
      onUpdate,
      record,
      rowKey,
    ],
  );

  return (
    <td {...restProps}>
      {isEditable ? (
        <Form.Item style={{ margin: 0 }} name={dataIndexKey} rules={rules}>
          {renderType === 'select' ? (
            <Select
              options={options}
              loading={isOptionsLoading}
              notFoundContent={
                isOptionsLoading ? <Spin size="small" /> : undefined
              }
              onChange={save}
            />
          ) : renderType === 'number' ? (
            <InputNumber
              onPressEnter={() => save()}
              onChange={deferUpdate ? save : undefined}
            />
          ) : (
            <Input
              onPressEnter={() => save()}
              onChange={deferUpdate ? (e) => save(e.target.value) : undefined}
            />
          )}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};
